import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Phone, Fire, Eye, CheckSquare, Square, CircleNotch, ArrowRight, Buildings } from '@phosphor-icons/react';
import { API_URL } from '../config/api';

/**
 * Call Candidates approval page.
 *
 * Each morning a scheduler picks prospects that showed buying signals
 * (clicks or 3+ opens in the last 24h), emails the admin a digest, and
 * exposes them here. The admin reviews the list, selects who to call,
 * and clicks "Llamar seleccionados" → Retell AI dials them.
 */
export default function CallCandidatesPage() {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/call-candidates`, { withCredentials: true });
      setCandidates(res.data?.candidates || []);
      setSelected(new Set());
    } catch (e) {
      window.alert('Error cargando candidatos: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    setSelected(selected.size === candidates.length ? new Set() : new Set(candidates.map(c => c.id)));
  };

  const approve = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Vas a llamar a ${selected.size} prospects. Retell AI marcará desde +1-786-436-7307. ¿Continuar?`)) return;
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/call-candidates/approve`,
        { prospect_ids: Array.from(selected) },
        { withCredentials: true },
      );
      window.alert(`✅ ${res.data.called} llamadas iniciadas correctamente.`);
      await load();
    } catch (e) {
      window.alert('Error: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><CircleNotch size={40} className="animate-spin text-blue-600" /></div>;
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="call-candidates-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Phone size={26} className="text-blue-600" weight="duotone" /> Candidatos para llamada
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Prospects que hicieron click o abrieron 3+ veces en las últimas 72 horas. Revisa, marca a quién llamar y aprueba — Retell AI hará la marcación automática.
        </p>
      </div>

      {candidates.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-10 text-center" data-testid="no-candidates">
          <Phone size={48} className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">No hay candidatos para llamar en este momento.</p>
          <p className="text-slate-500 text-sm mt-1">Cuando alguien abra o haga click en tus emails, aparecerán aquí.</p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="text-sm text-blue-900">
              <strong>{candidates.length}</strong> candidato{candidates.length !== 1 ? 's' : ''} con señal de interés
              {selected.size > 0 && <> · <strong>{selected.size}</strong> seleccionado{selected.size !== 1 ? 's' : ''}</>}
            </div>
            <div className="flex gap-2">
              <button
                onClick={toggleAll}
                data-testid="call-candidates-select-all"
                className="px-3 py-2 rounded-lg bg-white border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 transition"
              >
                {selected.size === candidates.length ? 'Deseleccionar todo' : 'Seleccionar todo'}
              </button>
              <button
                onClick={approve}
                disabled={selected.size === 0 || submitting}
                data-testid="call-candidates-approve-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {submitting ? <CircleNotch size={16} className="animate-spin" /> : <Phone size={16} />}
                Llamar a {selected.size || ''} seleccionado{selected.size !== 1 ? 's' : ''}
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500 w-8"></th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Contacto</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Empresa</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Teléfono</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-500">Señales</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-500">Score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {candidates.map((c) => {
                  const isSel = selected.has(c.id);
                  return (
                    <tr
                      key={c.id}
                      onClick={() => toggle(c.id)}
                      className={`cursor-pointer hover:bg-blue-50/60 transition ${isSel ? 'bg-blue-50' : ''}`}
                      data-testid={`candidate-row-${c.id}`}
                    >
                      <td className="px-4 py-3 align-middle">
                        {isSel ? <CheckSquare size={20} weight="fill" className="text-blue-600" /> : <Square size={20} className="text-slate-300" />}
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <div className="font-medium text-slate-900 text-sm">{c.contact_name || '(sin nombre)'}</div>
                        <div className="text-xs text-slate-500">{c.contact_email}</div>
                      </td>
                      <td className="px-4 py-3 align-middle text-sm text-slate-700">
                        <div className="flex items-center gap-1.5">
                          <Buildings size={14} className="text-slate-400" />
                          {c.company_name}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-middle font-mono text-sm text-slate-700">{c.contact_phone || '—'}</td>
                      <td className="px-4 py-3 align-middle">
                        <div className="flex items-center justify-center gap-2 text-xs">
                          {c.signal_clicks > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-100 text-red-800 font-bold">
                              <Fire size={12} weight="fill" /> {c.signal_clicks}
                            </span>
                          )}
                          {c.signal_opens > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-100 text-amber-800 font-bold">
                              <Eye size={12} weight="fill" /> {c.signal_opens}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-middle text-center">
                        <span className="text-lg font-bold text-slate-800">{c.signal_score || 0}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="text-xs text-slate-500 text-center pt-2">
            <ArrowRight size={12} className="inline" /> Las llamadas las hace Retell AI desde +1-786-436-7307. Las grabaciones y transcripciones aparecerán en <strong>Outbound Calls</strong>.
          </div>
        </>
      )}
    </div>
  );
}
